/**
 * Thin route components that mount each workspace section from shared context.
 */

import { lazy, Suspense, useMemo, type PropsWithChildren } from "react";
import { Navigate, useNavigate, useParams } from "@tanstack/react-router";

import { HasPlanFeature, type PlanFeatureCode } from "@libro-reclamos/contracts/Plans";

import { LoadBackofficeEnvironment } from "../../config/BackofficeEnvironment";
import { DashboardHome } from "../dashboard/components/organisms/DashboardHome";
import { useWorkspace } from "./WorkspaceContext";

const ArtifactAdministrationPage = lazy(async () => {
  const module = await import("../artifacts/pages/ArtifactAdministrationPage");
  return { default: module.ArtifactAdministrationPage };
});
const BookNoticePage = lazy(async () => {
  const module = await import("../book-notice/pages/BookNoticePage");
  return { default: module.BookNoticePage };
});
const CaseManagementPage = lazy(async () => {
  const module = await import("../cases/pages/CaseManagementPage");
  return { default: module.CaseManagementPage };
});
const FollowUpQueuePage = lazy(async () => {
  const module = await import("../follow-ups/pages/FollowUpQueuePage");
  return { default: module.FollowUpQueuePage };
});
const NotificationCenter = lazy(async () => {
  const module = await import("../notifications/components/organisms/NotificationCenter");
  return { default: module.NotificationCenter };
});
const CompanyProfilePage = lazy(async () => {
  const module = await import("../company-profile/pages/CompanyProfilePage");
  return { default: module.CompanyProfilePage };
});
const CompanyAdministrationPage = lazy(async () => {
  const module = await import("../platform/pages/CompanyAdministrationPage");
  return { default: module.CompanyAdministrationPage };
});
const EscalationAdministrationPage = lazy(async () => {
  const module = await import("../escalations/pages/EscalationAdministrationPage");
  return { default: module.EscalationAdministrationPage };
});
const CaseReasonAdministrationPage = lazy(async () => {
  const module = await import("../case-reasons/pages/CaseReasonAdministrationPage");
  return { default: module.CaseReasonAdministrationPage };
});
const TagAdministrationPage = lazy(async () => {
  const module = await import("../tags/pages/TagAdministrationPage");
  return { default: module.TagAdministrationPage };
});
const UserAdministrationPage = lazy(async () => {
  const module = await import("../users/pages/UserAdministrationPage");
  return { default: module.UserAdministrationPage };
});
const PlanAdministrationPage = lazy(async () => {
  const module = await import("../platform/pages/PlanAdministrationPage");
  return { default: module.PlanAdministrationPage };
});
const StarterHome = lazy(async () => {
  const module = await import("../dashboard/components/organisms/StarterHome");
  return { default: module.StarterHome };
});

function SectionFallback() {
  return (
    <p className="m-0 rounded-[var(--radius-medium)] border border-[var(--color-border-default)] bg-white p-8 text-center text-sm text-[var(--color-text-muted)]">
      Cargando sección…
    </p>
  );
}

const FeatureTitles: Readonly<Record<PlanFeatureCode, string>> = Object.freeze({
  API_ACCESS: "El acceso por API",
  BOOK_NOTICE: "El aviso del libro",
  BRANDING: "La identidad visual propia",
  CASE_REASONS: "Los motivos de reclamo",
  CUSTOM_DOMAIN: "Los dominios propios",
  ESCALATIONS: "La escalera de avisos",
  FOLLOW_UPS: "El seguimiento interno",
  IMPORTS: "Las importaciones",
  NOTIFICATIONS: "Las notificaciones",
  REASON_ANALYTICS: "La analítica de motivos",
  REPORTS: "Los reportes",
  TAGS: "Las etiquetas",
  USER_ADMINISTRATION: "La administración de usuarios",
  WHATSAPP_NOTICES: "Los avisos por WhatsApp",
});

/**
 * Renders the section only when the plan includes it.
 *
 * A customer who reaches the URL directly gets a plain explanation instead of
 * the API's rejection. Entitlements that have not loaded yet render the section:
 * the API still refuses the calls, so the worst case is an empty screen rather
 * than a wrongly locked one.
 */
function RequiresFeature({
  children,
  feature,
}: PropsWithChildren<{ readonly feature: PlanFeatureCode }>) {
  const { entitlements } = useWorkspace();
  if (entitlements === null || HasPlanFeature(entitlements, feature)) {
    return children;
  }
  return (
    <section className="grid gap-3 rounded-[var(--radius-medium)] border border-[var(--color-border-default)] bg-white p-8 text-center">
      <h2 className="m-0 text-base font-bold text-[var(--color-brand-deep)]">
        {FeatureTitles[feature]} no está en tu plan
      </h2>
      <p className="m-0 text-sm text-[var(--color-text-muted)]">
        Tu plan {entitlements.planName} no incluye esta sección. Escríbenos si quieres activarla.
      </p>
    </section>
  );
}

/**
 * Dashboard / inicio. A plan marked as simplified gets the guided starter home
 * instead of the full dashboard, so the smallest customer is not asked to read
 * analytics it never bought.
 */
export function HomeRoutePage() {
  const navigate = useNavigate();
  const { client, entitlements, expireSession, openCase, revision } = useWorkspace();
  const OpenQueue = () => {
    void navigate({ to: "/cases" });
  };
  if (entitlements?.usesSimplifiedConsole === true) {
    return (
      <Suspense fallback={<SectionFallback />}>
        <StarterHome
          canOpenBookNotice={HasPlanFeature(entitlements, "BOOK_NOTICE")}
          client={client}
          onOpenBookNotice={() => {
            void navigate({ to: "/book-notice" });
          }}
          onOpenCase={openCase}
          onOpenQueue={OpenQueue}
          onSessionExpired={expireSession}
          planName={entitlements.planName}
          revision={revision}
        />
      </Suspense>
    );
  }
  return (
    <DashboardHome
      client={client}
      onOpenCase={openCase}
      onOpenQueue={OpenQueue}
      onSessionExpired={expireSession}
      revision={revision}
      showReasonAnalytics={
        entitlements === null || HasPlanFeature(entitlements, "REASON_ANALYTICS")
      }
    />
  );
}

/**
 * Case queue, optionally focused on a public case id from the URL.
 */
export function CasesRoutePage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const publicId =
    typeof params.publicId === "string" && /^\d+$/.test(params.publicId) ? params.publicId : null;
  const { client, entitlements, expireSession, revision } = useWorkspace();
  return (
    <Suspense fallback={<SectionFallback />}>
      <CaseManagementPage
        client={client}
        initialPublicId={publicId}
        onCloseCase={() => {
          void navigate({ to: "/cases" });
        }}
        onOpenCase={(nextPublicId) => {
          void navigate({
            params: { publicId: nextPublicId },
            to: "/cases/$publicId",
          });
        }}
        onSessionExpired={expireSession}
        revision={revision}
        simplified={entitlements?.usesSimplifiedConsole === true}
      />
    </Suspense>
  );
}

/**
 * Global follow-up queue.
 */
export function FollowUpsRoutePage() {
  const { client, expireSession, openCase, revision } = useWorkspace();
  return (
    <RequiresFeature feature="FOLLOW_UPS">
      <Suspense fallback={<SectionFallback />}>
        <FollowUpQueuePage
          client={client}
          onOpenCase={openCase}
          onSessionExpired={expireSession}
          revision={revision}
        />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Recoverable notifications center.
 */
export function NotificationsRoutePage() {
  const { client, expireSession, openCase, revision } = useWorkspace();
  return (
    <RequiresFeature feature="NOTIFICATIONS">
      <Suspense fallback={<SectionFallback />}>
        <NotificationCenter
          client={client}
          onOpenCase={openCase}
          onSessionExpired={expireSession}
          revision={revision}
        />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Posted book notice (Anexo III) with its QR code.
 */
export function BookNoticeRoutePage() {
  const { client } = useWorkspace();
  return (
    <RequiresFeature feature="BOOK_NOTICE">
      <Suspense fallback={<SectionFallback />}>
        <BookNoticePage client={client} />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Company tag administration.
 */
export function TagsRoutePage() {
  const { client } = useWorkspace();
  return (
    <RequiresFeature feature="TAGS">
      <Suspense fallback={<SectionFallback />}>
        <TagAdministrationPage client={client} />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Case classification catalog. Reading it is enough to classify a case, so the
 * section stays open and the API denies the writes a non-admin attempts.
 */
export function CaseReasonsRoutePage() {
  const { client } = useWorkspace();
  return (
    <RequiresFeature feature="CASE_REASONS">
      <Suspense fallback={<SectionFallback />}>
        <CaseReasonAdministrationPage client={client} />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Notification escalation ladder; non-admins are sent home.
 */
export function EscalationsRoutePage() {
  const { canManageUsers, client } = useWorkspace();
  if (!canManageUsers) {
    return <Navigate replace to="/" />;
  }
  return (
    <RequiresFeature feature="ESCALATIONS">
      <Suspense fallback={<SectionFallback />}>
        <EscalationAdministrationPage client={client} />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Company user administration; non-admins are sent home.
 */
export function UsersRoutePage() {
  const { canManageUsers, client } = useWorkspace();
  if (!canManageUsers) {
    return <Navigate replace to="/" />;
  }
  return (
    <RequiresFeature feature="USER_ADMINISTRATION">
      <Suspense fallback={<SectionFallback />}>
        <UserAdministrationPage client={client} />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * Reports and imports administration.
 */
export function ArtifactsRoutePage() {
  const { client } = useWorkspace();
  return (
    <RequiresFeature feature="REPORTS">
      <Suspense fallback={<SectionFallback />}>
        <ArtifactAdministrationPage client={client} />
      </Suspense>
    </RequiresFeature>
  );
}

/**
 * The company administrator's own profile; anyone else is sent home.
 */
export function CompanyProfileRoutePage() {
  const { client, companyProfile, setCompanyProfile } = useWorkspace();
  const environment = useMemo(() => LoadBackofficeEnvironment(), []);
  if (companyProfile === undefined) {
    return <SectionFallback />;
  }
  if (companyProfile === null) {
    return <Navigate replace to="/" />;
  }
  return (
    <Suspense fallback={<SectionFallback />}>
      <CompanyProfilePage
        client={client}
        onProfileChanged={setCompanyProfile}
        portalBaseUrl={environment.portalBaseUrl}
        profile={companyProfile}
      />
    </Suspense>
  );
}

/**
 * Platform console; non-operators are sent home.
 */
export function PlatformRoutePage() {
  const { client, isPlatformOperator } = useWorkspace();
  const environment = useMemo(() => LoadBackofficeEnvironment(), []);
  if (!isPlatformOperator) {
    return <Navigate replace to="/" />;
  }
  return (
    <Suspense fallback={<SectionFallback />}>
      <CompanyAdministrationPage client={client} portalBaseUrl={environment.portalBaseUrl} />
    </Suspense>
  );
}

/**
 * Plan capability console; non-operators are sent home.
 */
export function PlansRoutePage() {
  const { client, isPlatformOperator } = useWorkspace();
  if (!isPlatformOperator) {
    return <Navigate replace to="/" />;
  }
  return (
    <Suspense fallback={<SectionFallback />}>
      <PlanAdministrationPage client={client} />
    </Suspense>
  );
}
